import React from 'react';
import { Box, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { TabbedOverviewPage, SectionHeader } from '../components/OverviewSections.comp';
import { NAVY, headCell, bodyCell, NoteBanner } from '../components/widgets.comp';

/* Slide 52 — How Priority Actions Are Triggered. This is common
   Introduction/Methodology content shared across every dashboard (per the
   Dashboard Common Section deck's own "PD, ND, SND, SD" tag on this exact
   page) — same content as Network Dashboard's identical page, which
   sourced it from the deck's own table/lists since the linked Google Doc
   (docs.google.com/document/d/184t8WL-PUC12...) returns 401 Unauthorized. */
const DECISION_LOGIC = [
    'Competencies gap → PbDQ Learning Program.',
    'Parent context gap → Parental Engagement.',
    'Teacher or school education gap → Teacher Empowerment.',
    'Serious risk or weak help-seeking → School Safeguarding & Support.',
    'Missing stakeholder evidence → Assessment Package.',
];

const ACTION_SCALE_LOGIC = [
    'One class affected → class action.',
    'Several classes in one grade → grade action.',
    'Pattern across school → whole-school action.',
    'Sensitive or serious risk → safeguarding-first action.',
];

const ACTION_PLAN_FAMILIES = [
    {family: 'PbDQ Learning Program',        purpose: 'Target learnable student competencies gaps'},
    {family: 'Parental Engagement',           purpose: 'Strengthen parent support and conversations'},
    {family: 'Teacher Empowerment',           purpose: 'Build teacher capability and response practice'},
    {family: 'School Safeguarding & Support', purpose: 'Protect, support, report, refer'},
    {family: 'Assessment Package',            purpose: 'Add missing parent/teacher/leader evidence'},
];

const LogicCard = ({title, items}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
    }}>
        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 2}}>{title}</Typography>
        <Box component="ul" sx={{m: 0, pl: 2.2}}>
            {items.map((item) => (
                <Box component="li" key={item} sx={{mb: 1}}>
                    <Typography sx={{fontSize: '13px', color: '#4B5563', lineHeight: 1.6}}>{item}</Typography>
                </Box>
            ))}
        </Box>
    </Box>
);

const FamiliesTable = () => (
    <TableContainer sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    }}>
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell sx={{...headCell, textAlign: 'left', pl: 2.5, width: '30%'}}>Family</TableCell>
                    <TableCell sx={{...headCell, textAlign: 'left'}}>Typical Use</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {ACTION_PLAN_FAMILIES.map((row, i) => (
                    <TableRow key={row.family} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                        <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.family}</TableCell>
                        <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.purpose}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const PriorityActionsSection = () => (
    <Box>
        <SectionHeader
            title="How Priority Actions Are Triggered"
            subtitle="How is the baseline evidence translated into action families, delivery scale, and concrete interventions?"
        />

        <Grid container spacing={3} alignItems="stretch" sx={{mb: 4}}>
            <Grid item xs={12} md={6}>
                <LogicCard title="Decision Logic" items={DECISION_LOGIC}/>
            </Grid>
            <Grid item xs={12} md={6}>
                <LogicCard title="Action Scale Logic" items={ACTION_SCALE_LOGIC}/>
            </Grid>
        </Grid>

        <Typography sx={{fontSize: '18px', fontWeight: 800, color: NAVY, mb: 1.5}}>
            Action Plan Families
        </Typography>
        <FamiliesTable/>

        <NoteBanner
            label="Rule:"
            text="The action engine should generate a recommendation first, then allow the school to confirm owner, timeline, and deployment status."
        />
    </Box>
);

const MethodologyPriorityActionsPage = () => (
    <TabbedOverviewPage
        skipNoDataGate
        tabs={[
            {
                label: 'How Priority Actions Are Triggered',
                content: <PriorityActionsSection/>,
            },
        ]}
    />
);

export default MethodologyPriorityActionsPage;
